import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { getTreatmentLaunchContext } from '../features/treatment-flow/launchContext';

const getCsrfToken = () => {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, 10) === ('csrftoken=')) {
        cookieValue = decodeURIComponent(cookie.substring(10));
        break;
      }
    }
  }
  return cookieValue;
};

const getAnalyticsApiUrl = () => {
  try {
    const tenantEndpoint = typeof window !== 'undefined'
      ? localStorage.getItem('welliemd_client_api_endpoint')
      : null;

    // Multi-tenant safe path: write analytics to the resolved tenant API.
    if (tenantEndpoint) {
      const normalized = tenantEndpoint.replace(/\/$/, '');
      return normalized.endsWith('/api/v1') ? normalized : `${normalized}/api/v1`;
    }
  } catch (err) {
    logTrackerError(err, 'Tenant Endpoint Resolution');
  }

  const url = process.env.REACT_APP_API_URL;

  // In production, avoid falling back to the control-plane API before tenant
  // config loads (this causes visitor sessions to be written to the wrong DB).
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  if (!url) {
    return "http://localhost:8000/api/v1";
  }
  return url;
};

// Centralized Logger
 
const logTrackerError = (error, context) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(`[Analytics Failure - ${context}]:`, error);
  }
  // Future integration point for Sentry/Datadog
};

export function useVisitorTracking() {
  const location = useLocation();
  const lastTrackedPath = useRef(null);

  useEffect(() => {
    if (getTreatmentLaunchContext(location.search).isAdminPreview) {
      return undefined;
    }
    let pendingRetryId = null;
    
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('analytics_session_id', sessionId);
    }

    // Secure Tracker Function
    const trackEvent = (eventType) => {
      try {
        const apiUrl = getAnalyticsApiUrl();
        if (!apiUrl) {
          return false;
        }

        const csrfToken = getCsrfToken();
        const headers = { 'Content-Type': 'application/json' };
        if (csrfToken) headers['X-CSRFToken'] = csrfToken;

        const isExistingPatientContext = Boolean(
          sessionStorage.getItem('existing_patient_session_id') ||
          sessionStorage.getItem('existing_patient_access_token') ||
          sessionStorage.getItem('followup_session_id') ||
          sessionStorage.getItem('followup_access_token')
        );

        axios.post(`${apiUrl}/analytics/track/`, {
          session_id: sessionId,
          path: location.pathname + location.search, // Use react-router location
          event_type: eventType,
          referrer: document.referrer,
          is_existing_patient: isExistingPatientContext,
        }, {
          withCredentials: true, 
          headers: headers       
        }).catch(err => logTrackerError(err, eventType));
        return true;
      } catch (err) {
        logTrackerError(err, 'Configuration Error');
        return false;
      }
    };

    // Deduplication Logic
    const currentPath = location.pathname + location.search;
    if (lastTrackedPath.current !== currentPath) {
      const isQuestionnaireRoute = !location.pathname.includes('/thank-you') && !location.pathname.includes('/checkout');
      if (isQuestionnaireRoute && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('welliemd:page_view', {
          detail: {
            path: currentPath,
            referrer: document.referrer,
          },
        }));
      }

      const sent = trackEvent('pageview');
      if (sent) {
        lastTrackedPath.current = currentPath;
      } else {
        // Client endpoint may not be available yet during app bootstrap.
        const retryId = setTimeout(() => {
          const retried = trackEvent('pageview');
          if (retried) {
            lastTrackedPath.current = currentPath;
          }
        }, 1500);
        // Ensure we clean up this retry timeout on route changes/unmount.
        pendingRetryId = retryId;
      }
    }

    // Heartbeat (Every 30s)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        trackEvent('heartbeat');
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      if (pendingRetryId) clearTimeout(pendingRetryId);
    };
  }, [location]); // Re-run securely on route change
}
